import { GDTF_REGEX } from "@coten-inc/utils/dist/gdtf/parser";
import type { GeoJSON } from "geojson";
import { FileText, Info, MapPin, Plus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { GdtfDateInput } from "~/components/gdtf-date-input";
import { SectionCard } from "~/components/section-card";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from "~/components/ui";
import { useEditMapTools } from "~/features/recordGeoItems/contexts/EditMapToolsContext";
import type { toAreaGeoItemEntryRows } from "~/features/recordGeoItems/hooks";
import type { RecordGeoItemFormData } from "~/features/recordGeoItems/types";
import { canEditRouteGeoJson } from "~/features/recordGeoItems/utils/routeEditorGeoJson";
import type { ResolutionType, ViewportType } from "~/lib/map/types";
import { isValidGeoJson } from "~/utils/isValidGeoJson";
import { AreaGeoItemEntryList } from "./AreaGeoItemEntryList";
import { EditMap, type EditMapHandles } from "./EditMap";
import { EditMapTools } from "./EditMapTools";
import { RouteEditorActions } from "./RouteEditorActions";
import { RouteEditorMap } from "./RouteEditorMap";

const MAX_RESOLUTION = 4;
type AreaInputMode = "h3" | "geoJson";

interface ReferenceOption {
  id: string;
  title: string;
  shortCode: string;
}

interface RecordGeoItemFormProps {
  references: ReferenceOption[];
  defaultValues: RecordGeoItemFormData;
  processing: boolean;
  submitLabel: string;
  /** Set when the record's Evidence describes the link: its record_geo_item properties are shown, not edited. */
  attributedEntries?: ReturnType<typeof toAreaGeoItemEntryRows>;
  onSubmit: (data: RecordGeoItemFormData) => void;
  onCancel: () => void;
}

export function RecordGeoItemForm({
  references,
  defaultValues,
  processing,
  submitLabel,
  attributedEntries,
  onSubmit,
  onCancel,
}: RecordGeoItemFormProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RecordGeoItemFormData>({ defaultValues });
  const evidences = useFieldArray({
    control,
    name: "evidences",
    rules: { required: { value: true, message: t("feature.recordGeoItems.form.errors.requiredEvidences") } },
  });

  const {
    comparisonAreas,
    pinnedLocations,
    setPinnedLocations,
    searchedLocation,
    setSearchedLocation,
    inViewportLocations,
    setViewport,
  } = useEditMapTools();
  const editMapRef = useRef<EditMapHandles>(null);

  const initialAreaInputMode: AreaInputMode =
    defaultValues.h3Indexes.length > 0 || !defaultValues.geometry ? "h3" : "geoJson";
  const [resolution, setResolution] = useState<ResolutionType>(3);
  const [routeEditorOpen, setRouteEditorOpen] = useState(
    initialAreaInputMode === "geoJson" && canEditRouteGeoJson(defaultValues.geometry),
  );
  const [areaInputMode, setAreaInputMode] = useState<AreaInputMode>(initialAreaInputMode);
  const h3IndexesDraftRef = useRef(defaultValues.h3Indexes);
  const geometryDraftRef = useRef(defaultValues.geometry);
  const [geoJsonText, setGeoJsonText] = useState(
    defaultValues.geometry ? JSON.stringify(defaultValues.geometry, null, 2) : "",
  );

  // `useWatch` (hook) instead of the `watch()` method: the method can't be memoized,
  // so React Compiler would skip memoizing this component and the values passed to <EditMap>.
  const h3Indexes = useWatch({ control, name: "h3Indexes" });
  const geometry = useWatch({ control, name: "geometry" });

  const toggleHexagon = (h3Index: string) => {
    const current = getValues("h3Indexes");
    const next = current.includes(h3Index) ? current.filter((i) => i !== h3Index) : [...current, h3Index];
    h3IndexesDraftRef.current = next;
    setValue("h3Indexes", next);
    clearErrors("geometry");
  };

  const onGeoJsonChange = (text: string) => {
    setGeoJsonText(text);
    if (!text.trim()) {
      geometryDraftRef.current = null;
      setValue("geometry", null);
      clearErrors("geometry");
      return;
    }
    if (!isValidGeoJson(text)) {
      setError("geometry", { message: t("feature.recordGeoItems.form.errors.invalidGeoJson") });
      return;
    }
    const next = JSON.parse(text) as GeoJSON;
    geometryDraftRef.current = next;
    setValue("geometry", next);
    setValue("h3Indexes", []);
    clearErrors("geometry");
  };

  const changeAreaInputMode = (nextMode: AreaInputMode) => {
    if (nextMode === areaInputMode) return;

    if (areaInputMode === "h3") {
      h3IndexesDraftRef.current = getValues("h3Indexes");
    } else {
      geometryDraftRef.current = getValues("geometry");
    }

    setAreaInputMode(nextMode);
    if (nextMode === "h3") {
      setRouteEditorOpen(false);
      setValue("h3Indexes", h3IndexesDraftRef.current);
      setValue("geometry", null);
      clearErrors("geometry");
      return;
    }

    setValue("h3Indexes", []);
    if (!geoJsonText.trim()) {
      setValue("geometry", null);
      clearErrors("geometry");
      setRouteEditorOpen(true);
    } else if (!isValidGeoJson(geoJsonText)) {
      setValue("geometry", null);
      setError("geometry", { message: t("feature.recordGeoItems.form.errors.invalidGeoJson") });
      setRouteEditorOpen(false);
    } else {
      setValue("geometry", geometryDraftRef.current);
      clearErrors("geometry");
      setRouteEditorOpen(canEditRouteGeoJson(geometryDraftRef.current));
    }
  };

  const onRouteEditorChange = (next: GeoJSON) => {
    geometryDraftRef.current = next;
    setValue("geometry", next);
    setGeoJsonText(JSON.stringify(next, null, 2));
    clearErrors("geometry");
  };

  const submit = (data: RecordGeoItemFormData) => {
    let valid = true;
    if (!attributedEntries && !data.jaLabel && !data.enLabel) {
      setError("jaLabel", { message: t("ui.required") });
      valid = false;
    }
    if (data.h3Indexes.length === 0 && !data.geometry) {
      setError("geometry", { message: t("feature.recordGeoItems.form.errors.eitherGeoJsonOrH3IndexesRequired") });
      valid = false;
    }
    if (errors.geometry) valid = false;
    if (!valid) return;
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-5">
      {attributedEntries ? (
        <SectionCard icon={Info} title={t("ui.detail")} contentClassName="space-y-3 text-[13px]">
          <p className="text-fg-2">{t("feature.recordGeoItems.form.attributedDescription")}</p>
          <AreaGeoItemEntryList entries={attributedEntries} />
        </SectionCard>
      ) : (
        <SectionCard icon={Info} title={t("ui.detail")} contentClassName="grid gap-4 sm:grid-cols-2">
          <Field label={t("model.recordGeoItem.i18nLabel.ja")} error={errors.jaLabel?.message}>
            <Input {...register("jaLabel")} />
          </Field>
          <Field label={t("model.recordGeoItem.i18nLabel.en")} error={errors.enLabel?.message}>
            <Input {...register("enLabel")} />
          </Field>
          <Controller
            control={control}
            name="date"
            rules={{
              required: t("ui.required"),
              pattern: { value: GDTF_REGEX, message: t("feature.recordGeoItems.form.errors.invalidGdtfFormat") },
            }}
            render={({ field, fieldState }) => (
              <GdtfDateInput
                label={t("ui.date")}
                value={field.value ?? ""}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="endDate"
            rules={{
              validate: (v) => !v || GDTF_REGEX.test(v) || t("feature.recordGeoItems.form.errors.invalidGdtfFormat"),
            }}
            render={({ field, fieldState }) => (
              <GdtfDateInput
                label={t("model.geoItem.endDate")}
                value={field.value ?? ""}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Field label={t("model.recordGeoItem.memo")} className="sm:col-span-2">
            <Textarea {...register("memo")} className="min-h-[4rem]" />
          </Field>
        </SectionCard>
      )}

      <SectionCard
        icon={MapPin}
        title={t("feature.recordGeoItems.form.area")}
        contentClassName="space-y-4"
        action={
          areaInputMode === "h3" ? (
            <div className="flex items-center gap-3">
              <Label className="whitespace-nowrap text-12-bold text-fg-2">
                {t("feature.recordGeoItems.form.resolution")}
              </Label>
              <Slider
                min={0}
                max={MAX_RESOLUTION}
                step={1}
                value={resolution}
                onChange={(e) => setResolution(Number(e.target.value) as ResolutionType)}
                className="w-40"
                aria-label={t("feature.recordGeoItems.form.resolution")}
              />
              <span className="w-3 text-12-bold tabular-nums text-fg-1">{resolution}</span>
            </div>
          ) : undefined
        }
      >
        <Tabs value={areaInputMode} onValueChange={(value) => changeAreaInputMode(value as AreaInputMode)}>
          <TabsList>
            <TabsTrigger value="h3">{t("feature.recordGeoItems.form.areaInputMode.h3")}</TabsTrigger>
            <TabsTrigger value="geoJson">{t("feature.recordGeoItems.form.areaInputMode.geoJson")}</TabsTrigger>
          </TabsList>
        </Tabs>
        {areaInputMode === "geoJson" && !routeEditorOpen && (
          <RouteEditorActions geometry={geometry} onStart={() => setRouteEditorOpen(true)} />
        )}
        {routeEditorOpen ? (
          <RouteEditorMap
            geometry={geometry}
            onChange={onRouteEditorChange}
            onClose={() => setRouteEditorOpen(false)}
          />
        ) : (
          <EditMap
            ref={editMapRef}
            resolution={resolution}
            areaH3Indexes={h3Indexes}
            geometry={geometry}
            enableHexagonSelection={areaInputMode === "h3"}
            onHexagonClick={toggleHexagon}
            comparisonAreas={comparisonAreas}
            pinnedLocations={pinnedLocations}
            searchedLocation={searchedLocation}
            inViewportLocations={inViewportLocations}
            onLocationClick={(location) => {
              setSearchedLocation(null);
              setPinnedLocations([...pinnedLocations, { ...location, isShow: true }]);
            }}
            onViewPortChange={(vp) => setViewport(vp as ViewportType)}
            tools={<EditMapTools onMoveTo={(coords) => editMapRef.current?.moveTo(coords)} />}
          />
        )}
        {areaInputMode === "geoJson" && (
          <Field label={t("model.recordGeoItem.geoJson")} error={errors.geometry?.message}>
            <Textarea
              value={geoJsonText}
              onChange={(e) => onGeoJsonChange(e.target.value)}
              disabled={routeEditorOpen}
              spellCheck={false}
              className="min-h-[6rem] font-mono text-[13px]"
            />
          </Field>
        )}
      </SectionCard>

      <SectionCard icon={FileText} title={t("model.evidence.modelNames")} contentClassName="space-y-3">
        {errors.evidences?.root && <p className="text-12-medium text-danger">{errors.evidences.root.message}</p>}
        {evidences.fields.map((field, index) => {
          const refError = errors.evidences?.[index]?.reference?.message;
          const pageError = errors.evidences?.[index]?.citation?.page?.message;
          return (
            <div key={field.id} className="flex items-start gap-2">
              <div className="flex-1">
                <Controller
                  control={control}
                  name={`evidences.${index}.reference`}
                  rules={{ required: t("ui.required") }}
                  render={({ field: refField }) => (
                    <Select
                      value={refField.value?.id ?? ""}
                      onValueChange={(id) => {
                        const ref = references.find((r) => r.id === id);
                        refField.onChange(ref ? { id: ref.id, title: ref.title } : null);
                      }}
                    >
                      <SelectTrigger
                        className="w-full"
                        aria-label={t("model.reference.modelName")}
                        aria-invalid={!!refError}
                      >
                        <SelectValue placeholder={t("model.reference.modelName")} />
                      </SelectTrigger>
                      <SelectContent>
                        {references.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.shortCode || r.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {refError && <p className="mt-1 text-12-medium text-danger">{refError}</p>}
              </div>
              <div className="w-32">
                <Input
                  {...register(`evidences.${index}.citation.page`, {
                    required: t("ui.required"),
                    pattern: { value: /^\d+$/, message: t("ui.numberOnly") },
                  })}
                  inputMode="numeric"
                  placeholder={t("model.evidence.page")}
                  aria-invalid={!!pageError}
                />
                {pageError && <p className="mt-1 text-12-medium text-danger">{pageError}</p>}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t("ui.delete")}
                onClick={() => evidences.remove(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => evidences.append({ reference: null, citation: { page: "" } })}
        >
          <Plus />
          <span>{t("ui.add")}</span>
        </Button>
      </SectionCard>

      <div className="flex items-center justify-between border-t border-border-default pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={processing}>
          {t("ui.cancel")}
        </Button>
        <Button type="submit" disabled={processing}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <Label className="text-12-bold capitalize text-fg-2">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-12-medium text-danger">{error}</p>}
    </div>
  );
}
